/**
 * BlogPostPage - Individual blog post page
 */

import { useParams, Navigate, Link } from "react-router-dom";
import { usePageTitle } from "../../hooks";
import { getPost, getRelatedPosts, getCategoryBySlug } from "../../lib/blog";
import {
  SEOHead,
  BlogContent,
  BlogPostHeader,
  RelatedPosts,
  LeadMagnetCTA,
} from "../../components/blog";
import { mdxComponents } from "../../components/blog/MDXComponents";

export function BlogPostPage() {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const post = getPost(category!, slug!);

  // Redirect to 404 if post not found
  if (!post) {
    return <Navigate to="/404" replace />;
  }

  const { frontmatter, Content } = post;
  usePageTitle(frontmatter.title);

  const relatedPosts = getRelatedPosts(post, 3);
  const categoryInfo = getCategoryBySlug(post.category);

  return (
    <div className="flex flex-col">
      <SEOHead
        title={frontmatter.title}
        description={frontmatter.description}
        canonical={`/blog/${category}/${slug}`}
        type="article"
        article={{
          publishedTime: frontmatter.date,
          author: frontmatter.author,
          section: categoryInfo?.name || post.category,
          tags: frontmatter.tags,
        }}
        image={frontmatter.image}
        imageAlt={frontmatter.imageAlt}
      />

      {/* Article Header */}
      <BlogPostHeader
        title={frontmatter.title}
        date={frontmatter.date}
        author={frontmatter.author}
        category={post.category}
        tags={frontmatter.tags}
      />

      {/* Article Content */}
      <article className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <BlogContent>
            <Content components={mdxComponents} />
          </BlogContent>
        </div>
      </article>

      {/* Lead Magnet CTA */}
      <section className="py-12 bg-sunshine-50">
        <div className="max-w-3xl mx-auto px-4">
          <LeadMagnetCTA category={post.category} />
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <RelatedPosts posts={relatedPosts} />
          </div>
        </section>
      )}

      {/* Back to Blog */}
      <section className="py-8 bg-neutral-50 border-t border-neutral-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 font-body text-body-md text-magic-600 hover:text-magic-700"
          >
            <span>←</span>
            Back to all posts
          </Link>
        </div>
      </section>
    </div>
  );
}

export default BlogPostPage;
